import type { WifiNetwork } from '../../api/wifi-types.js';
import { deviceNetworkLimit } from '../../device-ble/provision.js';
import type { AccountDevice, DeviceSync, SyncState } from '../sync/device-sync.js';
import { networkLimitNote, networksLabel } from '../wifi-text.js';
import { BOOT_HINT, NO_BLUETOOTH } from './device-sync.js';
import { h, type View } from './dom.js';
import { createNetworkPicker } from './wifi-network-picker.js';

export interface WifiSend extends View {
  setNetworks(networks: readonly WifiNetwork[]): void;
}

const PROGRESS = {
  sending: 'Enviando las redes protegidas…',
  verifying: 'Verificando la respuesta del aparato…',
  waiting_cloud: 'Redes guardadas. Esperando a que ASIS contacte con tu cuenta…',
} as const;

/** «Enviar al aparato»: reuses the Sincronizar state machine (Bluetooth chooser, BOOT window, receipts). Only network ids travel. */
export function createWifiSend(options: { sync: DeviceSync }): WifiSend {
  const { sync } = options;
  let networks: WifiNetwork[] = [];
  let sentCount = 0;
  let deviceKey = '';
  let alive = true;

  const picker = createNetworkPicker({ legend: 'Redes que quieres enviar', onChange: () => render(sync.getState()) });
  const select = h('select', { class: 'select', 'aria-label': 'Aparato de destino' });
  const find = h('button', { class: 'btn primary', type: 'button' }, ['Buscar el aparato']);
  const send = h('button', { class: 'btn primary', type: 'button', hidden: true }, ['Enviar redes']);
  const cancel = h('button', { class: 'btn ghost', type: 'button', hidden: true }, ['Cancelar']);
  const retry = h('button', { class: 'btn ghost', type: 'button', hidden: true }, ['Reintentar']);
  const choices = h('ul', { class: 'wireless-choices', 'aria-label': 'Aparatos encontrados', hidden: true });
  const status = h('p', { class: 'sync-status', role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' });
  const note = h('p', { class: 'card-sub wifi-send-note', 'aria-live': 'polite', hidden: true });
  const alert = h('p', { class: 'auth-error', role: 'alert', hidden: true });
  const hint = h('p', { class: 'card-sub sync-hint', hidden: true });
  const noNetworks = h('p', { class: 'card-sub' }, ['Guarda al menos una red arriba para poder enviarla.']);
  const noDevices = h('p', { class: 'card-sub', hidden: true }, ['Todavía no tienes aparatos propios en la cuenta. Añade uno desde Dispositivos.']);
  const root = h('section', { class: 'card wifi-send', 'aria-labelledby': 'wifi-send-title' }, [
    h('h2', { class: 'card-title', id: 'wifi-send-title' }, ['Enviar al aparato']),
    h('p', { class: 'card-sub' }, ['Elige el aparato y las redes que debe guardar. Las contraseñas viajan cifradas desde tu cuenta; tú no las ves ni las tecleas. Si el aparato solo guarda una red, se enviará la primera de tu selección.']),
    h('label', { class: 'field' }, [h('span', {}, ['Aparato']), select]), noDevices, noNetworks, picker.el,
    h('div', { class: 'form-row' }, [find, send, cancel, retry]), choices, status, note, alert, hint,
  ]);

  const owned = (state: SyncState): AccountDevice[] => state.account.devices.filter(device => device.status !== 'revoked' && (device.role ?? 'owner') === 'owner');

  function paintDevices(devices: AccountDevice[]): void {
    const key = JSON.stringify(devices.map(device => [device.id, device.name]));
    if (key === deviceKey) return;
    deviceKey = key;
    const previous = select.value;
    select.replaceChildren(h('option', { value: '' }, [devices.length ? 'Elige un aparato' : 'Sin aparatos']), ...devices.map(device => {
      const option = h('option', { value: device.id });
      option.textContent = device.name;
      return option;
    }));
    select.value = devices.some(device => device.id === previous) ? previous : devices.length === 1 ? devices[0]!.id : '';
  }

  function render(state: SyncState): void {
    if (!alive) return;
    const devices = owned(state);
    paintDevices(devices);
    noDevices.hidden = devices.length > 0 || state.account.status === 'loading' || state.account.status === 'idle' || state.account.status === 'error';
    noNetworks.hidden = networks.length > 0; picker.el.hidden = networks.length === 0;
    const target = select.value;
    const ids = picker.selected();
    const busy = state.phase === 'searching' || state.phase === 'adding';
    const link = state.ble.link?.info ?? null;
    const matches = Boolean(link && target && link.id === target);
    const names = ids.map(id => networks.find(network => network.id === id)?.ssid ?? '');
    select.disabled = busy; picker.setDisabled(busy);
    picker.setLimit(link && matches ? deviceNetworkLimit(link) : null);

    find.hidden = busy || matches;
    find.disabled = !target || ids.length === 0;
    find.textContent = link ? 'Buscar de nuevo' : 'Buscar el aparato';
    cancel.hidden = !busy;
    send.hidden = busy || !matches;
    const limit = link ? deviceNetworkLimit(link) : 1;
    const count = Math.min(ids.length, limit);
    send.textContent = count ? `Enviar ${networksLabel(count)}` : 'Enviar redes';
    send.disabled = count === 0;
    retry.hidden = state.account.status !== 'error' || busy;

    choices.hidden = Boolean(link) || state.ble.choices.length === 0 || state.phase === 'adding';
    choices.replaceChildren(...(link ? [] : state.ble.choices.map(choice => {
      const button = h('button', { class: 'btn ghost', type: 'button' });
      button.textContent = `Conectar con ${choice.deviceName}`;
      button.disabled = state.ble.selecting !== null;
      button.addEventListener('click', () => { void sync.selectChoice(choice.deviceId); });
      return h('li', {}, [button]);
    })));

    let text = '';
    if (state.add.status === 'working') text = PROGRESS[state.add.progress ?? 'sending'];
    else if (state.add.status === 'done') {
      const saved = sentCount === 1 ? 'La red se guardó' : `Las ${sentCount} redes se guardaron`;
      text = `${saved} en el aparato y su recibo está verificado.${state.add.cloudConfirmed ? ' ASIS ha vuelto a contactar con tu cuenta.' : ' Todavía no ha contactado con tu cuenta; puede tardar un poco.'}`;
    } else if (link) {
      const found = devices.find(device => device.id === link.id);
      text = matches ? `Aparato encontrado: ${found?.name ?? link.id}. Listo para enviar.`
        : found ? `Has encontrado «${found.name}», pero elegiste otro aparato. Cambia la elección o busca de nuevo.`
          : 'Este aparato no figura entre los tuyos. Añádelo antes desde Dispositivos.';
    } else if (state.ble.status === 'choosing') text = 'Elige tu aparato en la lista.';
    else if (state.ble.status === 'searching') text = 'Buscando aparatos cerca…';
    status.textContent = text;
    const limitText = link && matches ? networkLimitNote(names, limit) : null;
    note.textContent = limitText ?? ''; note.hidden = !limitText;
    const failure = state.account.error ?? state.ble.error ?? (['error', 'invalid', 'cancelled'].includes(state.add.status) ? state.add.message : null);
    alert.textContent = failure ?? ''; alert.hidden = !failure;
    const hintText = state.synced && state.ble.status === 'unavailable' ? NO_BLUETOOTH
      : state.synced && ['searching', 'none', 'expired', 'disconnected', 'error'].includes(state.ble.status) ? BOOT_HINT : '';
    hint.textContent = hintText; hint.hidden = !hintText;
  }

  // Web Bluetooth only opens its chooser inside the click that starts the search.
  find.addEventListener('click', () => { if (!find.disabled) void sync.start(); });
  send.addEventListener('click', () => {
    const ids = picker.selected(); const target = select.value;
    if (send.disabled || !ids.length || !target) return;
    sentCount = Math.min(ids.length, deviceNetworkLimit(sync.getState().ble.link?.info ?? {}));
    void sync.add({ deviceId: target, network: { savedNetworkIds: ids } });
  });
  cancel.addEventListener('click', () => { sync.cancel(); });
  retry.addEventListener('click', () => { void sync.refreshAccount(); });
  select.addEventListener('change', () => render(sync.getState()));
  const unsubscribe = sync.subscribe(render);
  render(sync.getState());

  return {
    el: root,
    setNetworks(next) { networks = [...next]; picker.setNetworks(networks); },
    destroy() { alive = false; unsubscribe(); picker.destroy?.(); },
  };
}
